import { logger } from "../utils/logger/logger";
import { Hook, HookEvents, RegisteredHooks } from "../types/types";
import { assertValidHook, assertValidHookName } from "../types/assertions";
import { getSafe } from "../utils/getSafe";
import { postMessageFromIframe } from "../frames/postMessageFromIframe";
import { EventEmitter, type EventArg } from "./eventEmitter";

class Hooks extends EventEmitter<HookEvents> {
  emit<K extends keyof HookEvents>(name: K, data: EventArg<HookEvents, K>) {
    // also emit to the iframe
    postMessageFromIframe({
      type: "hook",
      hook: { name, data },
    });
    super.emit(name, data);
  }
}

/**
 * This `hooks` object is the primary interaction point for listeners to events
 * inside the jslib. It's `on`, `once` and `off` methods allow interaction with
 * all `HookEvents`
 */
export const hooks = new Hooks();

/**
 * A helper function to give the deprecated addHooks function backwards
 * compatibility. It reaches into the internals of the Maps and Sets that make
 * the hooks emitter work. It sucks, and should go away when `addHook` does.
 *
 * @private
 */
function _getLegacyRegistry(): RegisteredHooks {
  const registry: RegisteredHooks = {};

  // eslint-disable-next-line no-restricted-syntax, dot-notation
  for (const [key, callbacks] of hooks["events"]) {
    // eslint-disable-next-line dot-notation
    registry[key] = [...callbacks["subscribers"].values()].map((callback) => ({
      name: key,
      callback,
    }));
  }

  return registry;
}

/**
 * @description Register a callback function (a "hook") that will be called for a given hook name.
 *      Hook names are called at specific points during execution and allow some form of extensibility.
 * @param hookName A specific string, one of the HookName type.
 * @param callback The function to call when the system performs certain actions, described by the hook's name.
 * @deprecated use `hooks.on()` instead
 */
export const addHook = (hook: Hook): RegisteredHooks => {
  assertValidHook(hook);

  hooks.on(hook.name, hook.callback);
  return _getLegacyRegistry();
};

/**
 * @description Retrieve a hook object that has been stored.
 * @param hookName
 * @deprecated
 */
export const getHook = <K extends keyof HookEvents>(hookName: K): Function => {
  assertValidHookName(hookName);
  return (data: HookEvents[K]) => {
    hooks.emit(hookName, data);
  };
};

/**
 * @description Look for an array of Hook objects that a developer may have provided in the window.sheerId object.
 *     If present, add each to the internal registry of hooks.
 *
 * @private
 * @returns true if any hooks were successfully registered.
 */
export const addPredefinedHooks = (): boolean => {
  let successCount = 0;
  getSafe(() => window.sheerId.hooks, []).forEach((hook: Hook) => {
    try {
      addHook(hook);
      successCount += 1;
    } catch (e) {
      logger.warn("Error adding hook:", hook, e);
    }
  });
  return successCount > 0;
};
